import { Dialog, Transition } from "@headlessui/react";
import { StarIcon } from "@heroicons/react/24/solid";
import { StarIcon as StarOutlineIcon } from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { submitFeedback } from "@/api/feedback";
import Button from "./button";

interface FeedbackPopupProps {
    isVisible: boolean;
    formType: string;
    onClose: () => void;
}

const STAR_CONFIG: Record<number, { label: string; emoji: string; color: string }> = {
    1: { label: "Poor",      emoji: "😞", color: "text-red-500" },
    2: { label: "Fair",      emoji: "😐", color: "text-orange-400" },
    3: { label: "Good",      emoji: "🙂", color: "text-yellow-500" },
    4: { label: "Very Good", emoji: "😊", color: "text-lime-500" },
    5: { label: "Excellent", emoji: "🤩", color: "text-green-500" },
};

export default function FeedbackPopup({ isVisible, formType, onClose }: FeedbackPopupProps) {
    const [stars, setStars] = useState(0);
    const [hovered, setHovered] = useState(0);
    const [feedbackText, setFeedbackText] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState("");
    const autoCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isMountedRef = useRef(true);
    const isClosingRef = useRef(false);

    const clearAutoCloseTimeout = useCallback(() => {
        if (!autoCloseTimeoutRef.current) {
            return;
        }

        clearTimeout(autoCloseTimeoutRef.current);
        autoCloseTimeoutRef.current = null;
    }, []);

    const handleClose = useCallback(() => {
        if (isClosingRef.current) {
            return;
        }

        isClosingRef.current = true;
        clearAutoCloseTimeout();
        setStars(0);
        setHovered(0);
        setFeedbackText("");
        setSubmitted(false);
        setError("");
        onClose();
    }, [clearAutoCloseTimeout, onClose]);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            clearAutoCloseTimeout();
        };
    }, [clearAutoCloseTimeout]);

    useEffect(() => {
        if (!isVisible) {
            clearAutoCloseTimeout();
            isClosingRef.current = false;
        }
    }, [clearAutoCloseTimeout, isVisible]);

    const { mutate, isPending: isLoading } = useMutation({
        mutationFn: () => submitFeedback({ form_type: formType, stars, feedback: feedbackText }),
        onSuccess: () => {
            setSubmitted(true);
            clearAutoCloseTimeout();
            autoCloseTimeoutRef.current = setTimeout(() => {
                if (!isMountedRef.current || !isVisible) {
                    return;
                }

                handleClose();
            }, 1800);
        },
        onError: () => {
            setError("Something went wrong. Please try again.");
        },
    });

    const displayRating = hovered || stars;
    const ratingInfo = STAR_CONFIG[displayRating];

    return (
        <Transition appear show={isVisible} as={Fragment}>
            <Dialog as="div" className="relative z-[300]" onClose={handleClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
                                {/* Header */}
                                <div className="bg-purple-800 px-6 py-4 flex items-center justify-between">
                                    <Dialog.Title className="text-lg font-semibold text-white">
                                        Share Your Feedback
                                    </Dialog.Title>
                                    <button
                                        onClick={handleClose}
                                        className="text-purple-200 hover:text-white transition-colors"
                                        aria-label="Close feedback"
                                    >
                                        <XMarkIcon className="w-5 h-5" />
                                    </button>
                                </div>

                                <div className="px-6 py-5">
                                    {submitted ? (
                                        /* Success state */
                                        <div className="text-center py-8">
                                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                                <svg className="w-9 h-9 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                            <p className="text-gray-800 font-semibold text-lg">Thank you!</p>
                                            <p className="text-gray-500 text-sm mt-1">Your feedback has been submitted successfully.</p>
                                        </div>
                                    ) : (
                                        <>
                                            {/* Form context */}
                                            <div className="mb-5">
                                                <p className="text-sm text-gray-500">You just submitted</p>
                                                <p className="text-base font-semibold text-purple-800 mt-0.5">{formType} Form</p>
                                            </div>

                                            {/* Star Rating */}
                                            <div className="mb-5">
                                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                                    How was your experience? <span className="text-red-500">*</span>
                                                </label>
                                                <div className="flex gap-2 items-center">
                                                    {[1, 2, 3, 4, 5].map((star) => (
                                                        <button
                                                            key={star}
                                                            type="button"
                                                            onClick={() => setStars(star)}
                                                            onMouseEnter={() => setHovered(star)}
                                                            onMouseLeave={() => setHovered(0)}
                                                            className="focus:outline-none transition-transform hover:scale-125 active:scale-110"
                                                        >
                                                            {star <= displayRating ? (
                                                                <StarIcon className="w-9 h-9 text-yellow-400 drop-shadow-sm" />
                                                            ) : (
                                                                <StarOutlineIcon className="w-9 h-9 text-gray-300" />
                                                            )}
                                                        </button>
                                                    ))}
                                                    {ratingInfo && (
                                                        <span className={`ml-2 text-sm font-semibold ${ratingInfo.color} flex items-center gap-1`}>
                                                            <span>{ratingInfo.emoji}</span>
                                                            <span>{ratingInfo.label}</span>
                                                        </span>
                                                    )}
                                                </div>
                                                {stars === 0 && (
                                                    <p className="text-xs text-gray-400 mt-2">Please select a rating to continue</p>
                                                )}
                                            </div>

                                            {/* message — shown after star is selected */}
                                            {stars > 0 && (
                                                <div className="mb-4 rounded-lg bg-purple-50 border border-purple-100 px-4 py-3">
                                                    <p className="text-sm text-purple-700 font-medium">
                                                        Your feedback is very important to us!
                                                    </p>
                                                    <p className="text-xs text-purple-500 mt-0.5">
                                                        Please share any suggestions or comments to help us improve your experience.
                                                    </p>
                                                </div>
                                            )}

                                            {/* Feedback Text */}
                                            <div className="mb-5">
                                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                                    Feedback <span className="text-gray-400 font-normal">(optional)</span>
                                                </label>
                                                <textarea
                                                    rows={4}
                                                    value={feedbackText}
                                                    onChange={(e) => setFeedbackText(e.target.value)}
                                                    placeholder="Tell us what we can do better..."
                                                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-700 placeholder-gray-400 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none resize-none transition-colors"
                                                    maxLength={2000}
                                                />
                                                <p className="text-xs text-gray-400 text-right mt-1">{feedbackText.length}/2000</p>
                                            </div>

                                              {error && (
                                                <p className="text-sm text-red-600 mb-3 text-center">{error}</p>
                                            )}

                                            {/* Actions */}
                                            <div className="flex justify-between items-center pt-1 border-t border-gray-100">
                                                <button
                                                    type="button"
                                                    onClick={handleClose}
                                                    className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                                                >
                                                    Skip for now
                                                </button>
                                                <Button
                                                    label={isLoading ? "Submitting..." : "Submit Feedback"}
                                                    type="button"
                                                    onClick={() => { if (stars > 0) mutate(); }}
                                                    disabled={stars === 0 || isLoading}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}
